import { Injectable, inject } from '@angular/core';
import { TrackingEventType } from '@newroom-connect/library/enums';
import { MediaSource } from '@newroom-connect/library/types';
import { TimeHelper } from '@newroom-connect/library/helpers';

import { TrackingEventService } from '../tracking-event/tracking-event.service';

export interface IVideoTrackingData {
  eventType: TrackingEventType;
  fileId: string;
  metadata?: string;
}

@Injectable()
export class VideoTrackingService {
  private readonly trackingEventService = inject(TrackingEventService);

  private currentFileId?: string | null;
  private currentFileSource?: MediaSource;
  private currentTime = 0;
  private currentVideoDuration?: number;

  /**
   * Handle video end event - tracks VIDEO_COMPLETE.
   */
  public handleVideoEnd(): void {
    this.trackEvent(TrackingEventType.VIDEO_COMPLETE);
  }

  /**
   * Handle video pause event - tracks VIDEO_PAUSE with progress.
   * @param currentTime Current playback time in seconds.
   */
  public handleVideoPause(currentTime: number): void {
    this.currentTime = currentTime;

    this.trackEvent(TrackingEventType.VIDEO_PAUSE, currentTime);
  }

  /**
   * Handle video play event - tracks VIDEO_PLAY with progress.
   * @param currentTime Current playback time in seconds.
   */
  public handleVideoPlay(currentTime: number | undefined): void {
    if (currentTime === undefined) {
      return;
    }

    this.currentTime = currentTime;

    this.trackEvent(TrackingEventType.VIDEO_PLAY, currentTime);
  }

  /**
   * Handle video seek event - tracks VIDEO_SEEK with progress.
   * @param currentTime Current playback time in seconds.
   */
  public handleVideoSeek(currentTime: number): void {
    this.currentTime = currentTime;

    this.trackEvent(TrackingEventType.VIDEO_SEEK, currentTime);
  }

  /**
   * Handle video duration loaded event - stores duration for progress calculations.
   * @param duration Video duration in seconds.
   */
  public handleVideoDuration(duration: number | undefined): void {
    this.currentVideoDuration = duration;
  }

  /**
   * Handle video destroy event - tracks VIDEO_DESTROY with progress.
   */
  public handleVideoDestroy(): void {
    this.trackEvent(TrackingEventType.VIDEO_DESTROY, this.currentTime);
  }

  /**
   * Set additional metadata to include in tracking events.
   *
   * @param fileId
   * @param fileSource
   */
  public setFileData(fileId: string | null, fileSource: MediaSource): void {
    this.currentFileId = fileId;
    this.currentFileSource = fileSource;
  }

  /**
   * Sends tracking event with optional progress information.
   *
   * @param eventType Type of tracking event to send.
   * @param currentTime Optional current playback time in seconds for progress tracking.
   */
  private trackEvent(eventType: TrackingEventType, currentTime?: number): void {
    if (!this.currentFileId) {
      return;
    }

    const trackingData: IVideoTrackingData = {
      eventType: eventType,
      fileId: this.currentFileId,
      metadata: JSON.stringify({
        mimeType: this.currentFileSource?.mimetype,
        totalDuration: this.currentVideoDuration ? TimeHelper.formatTime(this.currentVideoDuration) : null,
        currentTime: currentTime ? TimeHelper.formatTime(currentTime) : null
      })
    };

    this.trackingEventService.createTrackingEvent(trackingData);
  }
}
